<script lang="ts">
	import classNames from 'classnames';
	import { NewsRoutes } from '../../constants/routes';
	import type { Category } from './MenuCategoryTag/types';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { getNewsCategoryUrl } from '#lib/features/news/utils/getNewsCategoryUrl/getNewsCategoryUrl.js';
	import { page } from '$app/state';
	import { ShoutoutIcon } from '$global/assets/icons/ShoutoutIcon';
	import { Container } from '$global/components/Container';
	import { Txt } from '$global/components/Txt';
	import { isElementVisibleInViewport } from '$global/utils/isElementVisibleInViewport/isElementVisibleInViewport';

	const dataCyPrefix = 'news-category-tags';
	interface Props {
		categories: Category[];
		forceShowGlobalNav?: boolean | undefined;
	}

	let { categories, forceShowGlobalNav = undefined }: Props = $props();

	// The URL param is the fallback authority: on error pages (and any route
	// whose load doesn't provide showGlobalNav) page.data is empty, and
	// app-mode (global_nav=0) visitors must stay on navless links.
	let showGlobalNav = $derived(
		forceShowGlobalNav ??
			page.data.showGlobalNav ??
			page.url.searchParams.get('global_nav') !== '0'
	);
	let ref: HTMLDivElement | null = $state(null);

	$effect(() => {
		if (ref) {
			const category = page.url.searchParams.get('category') || 'top';
			const elt = document.getElementById(
				`news-category-${category.toLowerCase()}`
			);

			if (elt && !isElementVisibleInViewport(elt)) {
				ref.scrollLeft = elt.offsetLeft;
			}
		}
	});
</script>

{#snippet categorySnippet(
	category: Category,
	i: number,
	nbCategories: number,
	href?: string
)}
	<a
		data-cy={`${dataCyPrefix}-menu-category-tag`}
		id={`news-category-${category.label.toLowerCase()}`}
		href={(() => {
			if (href) {
				return `${href}${showGlobalNav ? `` : `?global_nav=0`}`;
			}

			if (category.label === 'top') {
				return `${GlobalRoutes.NewsHome}${
					showGlobalNav ? `` : `?global_nav=0`
				}`;
			}

			return getNewsCategoryUrl(showGlobalNav, category.label);
		})()}
		class={classNames(
			'relative flex flex-1 justify-center border-gray-200 px-4 hover:bg-gray-50',
			{
				'border-r': i < nbCategories - 1 || i === 1,
				'lg:border-l': !i,
				'lg:border-r': i === nbCategories - 1,
				'border-l-0': category.label === 'data',
			}
		)}
	>
		<div class="flex items-center gap-1.5">
			{#if category.label === 'top'}
				<ShoutoutIcon color="gray-900" class="h-4 w-4" />
			{/if}

			<Txt variant="body3" class="py-4 tracking-widest uppercase">
				{category.label}
			</Txt>
		</div>

		{#if category.active}
			<div
				class="absolute bottom-0 left-0 h-[0.125rem] w-full bg-gray-900"
			></div>
		{/if}
	</a>
{/snippet}

<div
	class={classNames(
		'fixed top-12 z-[25] w-full border-gray-200 bg-white py-0 lg:border-b',
		{
			'lg:top-[3.5rem]': !showGlobalNav,
			'lg:top-[6.5rem]': showGlobalNav,
		}
	)}
>
	<Container
		dataCy={dataCyPrefix}
		class="border-b border-gray-200 px-0 sm:px-0 lg:border-b-0 lg:px-6"
	>
		<div bind:this={ref} class="no-scrollbar flex overflow-scroll">
			{#each categories.slice(0, 3) as category, i (category.label)}
				{@render categorySnippet(category, i, categories.length)}
			{/each}
			{@render categorySnippet(
				{ label: 'data', active: page.route.id === NewsRoutes.Data },
				1,
				0,
				NewsRoutes.Data
			)}
			{#each categories.slice(3) as category, i (category.label)}
				{@render categorySnippet(category, i + 3, categories.length)}
			{/each}
		</div>
	</Container>
</div>
